import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { THERAPIST_STATUS } from '../types/constants';

const ProtectedRoute: React.FC = () => {
  const { user, therapist, loading, therapistLoaded, error } = useAuth();
  const location = useLocation();

  if (loading || !therapistLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F8FA]">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#7F56D9] border-t-transparent"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-[#F8F8FA]">
        <div className="bg-red-50 p-4 rounded-lg text-red-700">{error}</div>
      </div>
    );
  }

  if (!therapist) {
    console.log('Redirecting to login (no therapist)');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (therapist.status === THERAPIST_STATUS.NEW_CLIENT && location.pathname !== '/pending') {
    return <Navigate to="/pending" replace />;
  }

  if (
    therapist.status === THERAPIST_STATUS.DEACTIVATED &&
    location.pathname !== '/deactivated'
  ) {
    return <Navigate to="/deactivated" replace />;
  }

  // 3. Handle TRIAL EXPIRED
  if (therapist.status === THERAPIST_STATUS.TRIAL) {
    const now = Date.now(); // Current time in milliseconds
    // Ensure packageExpiryDate exists and is a Firestore Timestamp-like object
    const packageExpiryDateTime = therapist.packageExpiryDate?.toMillis ? therapist.packageExpiryDate.toMillis() : null;

    if (
      therapist.currentPackageId === 'trial' && // Ensure it's the specific 'trial' package
      packageExpiryDateTime &&                   // Ensure expiry date is valid
      now >= packageExpiryDateTime &&            // Check if current time is past expiry time
      location.pathname !== '/trial-expired'     // Avoid redirect loop
    ) {
      console.log('Trial expired, redirecting to /trial-expired');
      return <Navigate to="/trial-expired" replace />;
    }
  }

  // 4. Handle ACTIVE or (Non-Expired TRIAL)
  if (therapist.status === THERAPIST_STATUS.ACTIVE) {
    return <Outlet />;
  }

  if (therapist.status === THERAPIST_STATUS.TRIAL) {
    const now = Date.now();
    const packageExpiryDateTime = therapist.packageExpiryDate?.toMillis ? therapist.packageExpiryDate.toMillis() : null;

    // Allow access if it's a trial and (it has no expiry date OR the expiry date is in the future)
    if (therapist.currentPackageId === 'trial' && (!packageExpiryDateTime || now < packageExpiryDateTime)) {
      return <Outlet />;
    }
    // If it's a trial but somehow didn't get caught by the expired check above and isn't valid here,
    // it will fall through to the final redirect
  }

  return <Navigate to="/login" state={{ from: location }} replace />;
};

export default ProtectedRoute;